import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import { DateField, DropdownField } from './FormFields';
import {
  createEpisode,
  getAvailableClinicians,
  listAgencyClinicPhysicians,
  listPatientInsurances,
} from '../services/api';
import type {
  AvailableClinicianResponse,
  ClinicPhysicianResponse,
  EpisodeSummaryResponse,
  PatientInsuranceResponse,
} from '../services/api/types.gen';
import { useToast } from '../hooks/useToastContext';
import {
  formatDateToISO,
  getTodayInPT,
  parseISOToLocalDate,
} from '../utils/dateUtils';
import {
  parse4xxApiError,
  parse4xxHeyApiResult,
} from '../utils/apiErrorDetail';
import { cliniciansToSelectOptions } from '../utils/clinicianUtils';
import { formatPhysicianName } from '../utils/formatting';
import { patientInsuranceSectionPath } from '../utils/navigation';
import { MAX_EPISODE_END_DATE_OFFSET } from '../utils/episodeBoundaryVisits';

type DisabledDateRange = { from: Date; to: Date };

type Props = {
  open: boolean;
  onClose: () => void;
  patientId: string;
  clinicId: string;
  previousEpisode: EpisodeSummaryResponse | null;
  existingEpisodes: EpisodeSummaryResponse[];
  onCreated: (newEpisode: EpisodeSummaryResponse) => void;
};

function isoAddDays(iso: string, days: number): string {
  const date = parseISOToLocalDate(iso);
  if (!date) return iso;
  date.setDate(date.getDate() + days);
  return formatDateToISO(date);
}

function initialStartDate(previousEpisode: EpisodeSummaryResponse | null) {
  return previousEpisode
    ? isoAddDays(previousEpisode.end_date, 1)
    : getTodayInPT();
}

function isoToDate(iso: string): Date {
  return parseISOToLocalDate(iso) ?? new Date(`${iso}T00:00:00`);
}

function maxEpisodeEndDate(startDate: string): string {
  return isoAddDays(startDate, MAX_EPISODE_END_DATE_OFFSET);
}

function patientInsuranceLabel(insurance: PatientInsuranceResponse): string {
  if (insurance.plan_name) {
    return `${insurance.insurance_name} — ${insurance.plan_name}`;
  }
  return insurance.insurance_name;
}

function episodeRangeToMatcher(
  episode: EpisodeSummaryResponse
): DisabledDateRange | null {
  const from = parseISOToLocalDate(episode.start_date);
  const to = parseISOToLocalDate(episode.end_date);
  if (!from || !to) return null;
  return { from, to };
}

export default function AddEpisodeModal({
  open,
  onClose,
  patientId,
  clinicId,
  previousEpisode,
  existingEpisodes,
  onCreated,
}: Props) {
  const navigate = useNavigate();
  const { error: showError } = useToast();
  const [startDate, setStartDate] = useState(() =>
    initialStartDate(previousEpisode)
  );
  const [endDate, setEndDate] = useState(() => maxEpisodeEndDate(startDate));
  const [patientInsuranceId, setPatientInsuranceId] = useState<string | null>(
    previousEpisode?.patient_insurance_id ?? null
  );
  const [assignedClinicianId, setAssignedClinicianId] = useState<string | null>(
    previousEpisode?.assigned_clinician_id ?? null
  );
  const [caseManagerId, setCaseManagerId] = useState<string | null>(
    previousEpisode?.case_manager_id ?? null
  );
  const [clinicPhysicianId, setClinicPhysicianId] = useState<string | null>(
    previousEpisode?.clinic_physician_id ?? null
  );
  const [completePrevious, setCompletePrevious] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [insurances, setInsurances] = useState<PatientInsuranceResponse[]>([]);
  const [insurancesLoading, setInsurancesLoading] = useState(false);
  const [clinicians, setClinicians] = useState<AvailableClinicianResponse[]>(
    []
  );
  const [physicians, setPhysicians] = useState<ClinicPhysicianResponse[]>([]);

  useEffect(() => {
    if (!open) return;

    const nextStart = initialStartDate(previousEpisode);
    setStartDate(nextStart);
    setEndDate(maxEpisodeEndDate(nextStart));
    setPatientInsuranceId(previousEpisode?.patient_insurance_id ?? null);
    setAssignedClinicianId(previousEpisode?.assigned_clinician_id ?? null);
    setCaseManagerId(previousEpisode?.case_manager_id ?? null);
    setClinicPhysicianId(previousEpisode?.clinic_physician_id ?? null);
    setCompletePrevious(true);
  }, [open, previousEpisode]);

  useEffect(() => {
    if (!open || !patientId) return;

    let cancelled = false;
    const loadPatientInsurances = async () => {
      setInsurancesLoading(true);
      try {
        const insuranceResponse = await listPatientInsurances({
          path: { patient_id: patientId },
        });

        if (cancelled) return;
        setInsurances(insuranceResponse.data ?? []);
      } catch (error) {
        console.error('Failed to load patient insurances:', error);
        if (!cancelled) {
          setInsurances([]);
        }
      } finally {
        if (!cancelled) {
          setInsurancesLoading(false);
        }
      }
    };

    void loadPatientInsurances();

    return () => {
      cancelled = true;
    };
  }, [patientId, open]);

  useEffect(() => {
    if (!open || !clinicId) return;

    let cancelled = false;
    const loadClinicOptions = async () => {
      try {
        const [clinicianResponse, physicianResponse] = await Promise.all([
          getAvailableClinicians({
            query: { clinic_id: clinicId },
          }),
          listAgencyClinicPhysicians({
            path: { agency_clinic_id: clinicId },
          }),
        ]);

        if (cancelled) return;
        setClinicians(clinicianResponse.data ?? []);
        setPhysicians(physicianResponse.data ?? []);
      } catch (error) {
        console.error('Failed to load add episode options:', error);
        if (!cancelled) {
          setClinicians([]);
          setPhysicians([]);
        }
      }
    };

    void loadClinicOptions();

    return () => {
      cancelled = true;
    };
  }, [clinicId, open]);

  const showCompletePrevious =
    previousEpisode !== null &&
    previousEpisode.status === 'active' &&
    previousEpisode.end_date < startDate;

  // Cancelled ("Inactive") episodes are retired and must not block a replacement
  // episode over the same dates (EMR-158). Filter once here so the disabled-date
  // pickers and the overlap check all agree.
  const activeExistingEpisodes = useMemo(
    () => existingEpisodes.filter(episode => episode.status !== 'cancelled'),
    [existingEpisodes]
  );

  const existingEpisodeDateRanges = useMemo(
    () =>
      activeExistingEpisodes
        .map(episodeRangeToMatcher)
        .filter((range): range is DisabledDateRange => range !== null),
    [activeExistingEpisodes]
  );
  const minEndDate = isoAddDays(startDate, 1);
  const maxEndDate = maxEpisodeEndDate(startDate);
  const endDateDisabledRanges = useMemo(() => {
    const minEnd = minEndDate;
    const maxEnd = maxEndDate;

    return activeExistingEpisodes
      .map(episode => {
        if (episode.end_date < minEnd || episode.start_date > maxEnd) {
          return null;
        }

        const from = isoToDate(
          episode.start_date > minEnd ? episode.start_date : minEnd
        );
        const to = isoToDate(
          episode.end_date < maxEnd ? episode.end_date : maxEnd
        );

        return { from, to };
      })
      .filter((range): range is { from: Date; to: Date } => range !== null);
  }, [activeExistingEpisodes, maxEndDate, minEndDate]);

  const overlap = useMemo(
    () =>
      activeExistingEpisodes.find(
        episode =>
          startDate <= episode.end_date && endDate >= episode.start_date
      ),
    [endDate, activeExistingEpisodes, startDate]
  );

  const dateOrderInvalid = endDate <= startDate;
  const episodeTooLong = endDate > maxEndDate;
  const hasPatientInsurances = insurances.length > 0;
  const insuranceNotSelected = hasPatientInsurances && !patientInsuranceId;
  const submitDisabled =
    submitting ||
    insurancesLoading ||
    !hasPatientInsurances ||
    insuranceNotSelected ||
    dateOrderInvalid ||
    episodeTooLong ||
    Boolean(overlap);

  const handleGoToPatientInsurance = () => {
    onClose();
    navigate(patientInsuranceSectionPath(patientId));
  };

  const handleStartDateChange = (value: string | null) => {
    if (!value) return;
    setStartDate(value);
    setEndDate(maxEpisodeEndDate(value));
  };

  const handleSubmit = async () => {
    if (submitDisabled) return;

    setSubmitting(true);
    try {
      const result = await createEpisode({
        body: {
          patient_id: patientId,
          start_date: startDate,
          end_date: endDate,
          patient_insurance_id: patientInsuranceId,
          assigned_clinician_id: assignedClinicianId,
          case_manager_id: caseManagerId,
          clinic_physician_id: clinicPhysicianId,
          complete_previous_episode: showCompletePrevious && completePrevious,
        },
        throwOnError: false,
      });

      if (!result.data) {
        const { message } = parse4xxHeyApiResult(result);
        showError(message ?? 'Failed to add episode');
        return;
      }

      onCreated(result.data);
      onClose();
    } catch (error) {
      console.error('Failed to add episode:', error);
      const { message } = parse4xxApiError(error);
      showError(message ?? 'Failed to add episode');
    } finally {
      setSubmitting(false);
    }
  };

  const clinicianOptions = cliniciansToSelectOptions(clinicians, 'clinician');
  const caseManagerOptions = cliniciansToSelectOptions(
    clinicians,
    'caseManager'
  );
  const physicianOptions = physicians.map(physician => ({
    value: physician.id,
    label: formatPhysicianName(physician),
  }));

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onClose()}>
      <DialogContent className='w-[calc(100vw-2rem)] sm:max-w-xl'>
        <DialogHeader>
          <DialogTitle>Add Episode</DialogTitle>
          <DialogDescription className='sr-only'>
            Create a new episode for this patient.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4 py-2'>
          <div>
            <DateField
              label='Start date'
              htmlFor='add-episode-start-date'
              data-testid='add-episode-start-date'
              value={startDate}
              onChange={handleStartDateChange}
              fieldSize='default'
              disabledDates={existingEpisodeDateRanges}
            />
          </div>

          <div>
            <DateField
              label='End date'
              htmlFor='add-episode-end-date'
              data-testid='add-episode-end-date'
              value={endDate}
              onChange={(value: string | null) => {
                if (!value) return;
                setEndDate(value);
              }}
              fieldSize='default'
              minDate={isoToDate(minEndDate)}
              maxDate={isoToDate(maxEndDate)}
              disabledDates={endDateDisabledRanges}
            />
            {dateOrderInvalid && (
              <p className='mt-1 text-sm text-destructive'>
                End date must be after start date.
              </p>
            )}
            {episodeTooLong && (
              <p className='mt-1 text-sm text-destructive'>
                Episode cannot be longer than 60 days.
              </p>
            )}
          </div>

          {insurancesLoading ? (
            <div>
              <Label htmlFor='add-episode-insurance'>Insurance</Label>
              <p className='mt-1 text-sm text-muted-foreground'>
                Loading insurances…
              </p>
            </div>
          ) : hasPatientInsurances ? (
            <DropdownField
              label='Insurance'
              htmlFor='add-episode-insurance'
              variant='formSelect'
              sortOptions
              placeholder='None'
              options={insurances.map(insurance => ({
                value: insurance.id,
                label: patientInsuranceLabel(insurance),
              }))}
              value={patientInsuranceId ?? ''}
              onChange={value => setPatientInsuranceId(value || null)}
            />
          ) : (
            <div>
              <Label>Insurance</Label>
              <button
                type='button'
                onClick={handleGoToPatientInsurance}
                className='mt-1 text-sm text-primary hover:underline text-left'
              >
                Add an insurance entry for this patient before you can create a
                new episode.
              </button>
            </div>
          )}

          <DropdownField
            label='Assigned clinician'
            htmlFor='add-episode-assigned-clinician'
            variant='formSelect'
            sortOptions
            placeholder='Unassigned'
            options={clinicianOptions}
            value={assignedClinicianId ?? ''}
            onChange={value => setAssignedClinicianId(value || null)}
          />

          <DropdownField
            label='Case manager'
            htmlFor='add-episode-case-manager'
            variant='formSelect'
            sortOptions
            placeholder='Unassigned'
            options={caseManagerOptions}
            value={caseManagerId ?? ''}
            onChange={value => setCaseManagerId(value || null)}
          />

          <DropdownField
            label='Physician'
            htmlFor='add-episode-physician'
            variant='formSelect'
            sortOptions
            placeholder='None'
            options={physicianOptions}
            value={clinicPhysicianId ?? ''}
            onChange={value => setClinicPhysicianId(value || null)}
          />

          {showCompletePrevious && (
            <div className='flex items-center gap-2'>
              <Checkbox
                id='ae-complete-previous'
                checked={completePrevious}
                onCheckedChange={value => setCompletePrevious(Boolean(value))}
              />
              <Label htmlFor='ae-complete-previous'>
                Mark previous episode as completed
              </Label>
            </div>
          )}

          {overlap && (
            <p className='text-sm text-destructive'>
              Overlaps episode {overlap.start_date} - {overlap.end_date}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          {insuranceNotSelected ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className='inline-flex'>
                    <Button onClick={handleSubmit} disabled>
                      {submitting ? 'Adding...' : 'Add Episode'}
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>An insurance must be selected</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <Button onClick={handleSubmit} disabled={submitDisabled}>
              {submitting ? 'Adding...' : 'Add Episode'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
